import { MoreVert } from "@mui/icons-material";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { showPrompt } from "@samdesk/components/Prompt";
import useCreateDashboardTab from "api/mutations/useCreateDashboardTab";
import useDeleteDashboardTab from "api/mutations/useDeleteDashboardTab";
import useUpdateDashboardTab from "api/mutations/useUpdateDashboardTab";
import { AlertsTabData } from "autoGenSubTypes";
import { DashboardTabType } from "gql/graphql";
import { useState } from "react";
import theme from "theme";
import { useDeleteMapState } from "ui-library/Map/useDeleteMapState";
import useNavigateWithAccount from "utils/useNavigateWithAccount";
import useSnackbar from "utils/useSnackbar";
import useNavigateAfterTabDelete from "./useNavigateAfterTabDelete";

interface Props {
	alertsData: AlertsTabData;
	tabKey: string;
}

const TabMenu = ({ alertsData, tabKey }: Props) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const {
		mutateAsync: createDashboardTab,
		isLoading: isLoadingCreateDashboardTab,
	} = useCreateDashboardTab();
	const {
		mutateAsync: updateDashboardTab,
		isLoading: isLoadingUpdateDashboardTab,
	} = useUpdateDashboardTab();
	const {
		mutateAsync: deleteDashboardTab,
		isLoading: isLoadingDeleteDashboardTab,
	} = useDeleteDashboardTab();

	const { deleteMapState } = useDeleteMapState();

	const navigate = useNavigateWithAccount();
	const navigateAfterTabDelete = useNavigateAfterTabDelete();

	const { handleError } = useSnackbar();

	/** Menu handlers */
	const handleClickRename = async () => {
		setAnchorEl(null);

		const { state, value: newTabName } = await showPrompt({
			promptType: "input",
			title: "Rename Tab",
		});

		if (state === "ok" && newTabName) {
			await handleSubmitRename(newTabName);
		}
	};

	const handleClickDelete = async () => {
		setAnchorEl(null);
		const confirmPrompt = await showPrompt({
			promptType: "confirm",
			title: "Are you sure you want to delete this tab?",
			description: "Your streams will remain available in the stream manager.",
			confirmButton: { label: "Delete", intent: "destruction" },
		});

		if (confirmPrompt.state === "ok") {
			await handleSubmitDelete();
		}
	};

	/** Response handlers */
	const handleSubmitRename = async (newTabName: string) => {
		try {
			const response = await updateDashboardTab({
				key: tabKey,
				tab: {
					alerts_tab_data: {
						bottom: alertsData.bottom,
						name: newTabName,
						top: alertsData.top,
					},
					type: DashboardTabType.ALERTS,
				},
			});
			if (response.error) {
				handleError(response.error);
			}
		} catch (error) {
			handleError(error);
		}
	};

	const handleSubmitDelete = async () => {
		if (!tabKey) return;

		try {
			const response = await deleteDashboardTab({ params: { key: tabKey } });
			if (response.success) {
				deleteMapState(tabKey);
				navigateAfterTabDelete(tabKey);
			} else if (response.error) {
				handleError(response.error);
			}
		} catch (error) {
			handleError(error);
		}
	};

	const handleSubmitDuplicate = async () => {
		try {
			const response = await createDashboardTab({
				params: {
					tab: {
						alerts_tab_data: {
							bottom: alertsData.bottom,
							name: alertsData.name,
							top: alertsData.top,
						},
						type: DashboardTabType.ALERTS,
					},
				},
			});
			if (response.success && response.tab) {
				navigate(`/alerts/${response.tab.key}`);
			}
		} catch (error) {
			handleError(error);
		}

		setAnchorEl(null);
	};

	const isBusy =
		isLoadingCreateDashboardTab ||
		isLoadingUpdateDashboardTab ||
		isLoadingDeleteDashboardTab;

	return (
		<>
			<IconButton
				disabled={isBusy}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			>
				<MoreVert fontSize="small" />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				open={Boolean(anchorEl)}
				sx={tabDropdownSx}
			>
				<MenuItem onClick={handleClickRename}>Rename Tab</MenuItem>
				<MenuItem onClick={handleClickDelete}>Delete Tab</MenuItem>
				<MenuItem onClick={handleSubmitDuplicate}>Duplicate Tab</MenuItem>
			</Menu>
		</>
	);
};

export default TabMenu;

/** Styles */

const tabDropdownSx = {
	"& .MuiMenu-list": {
		background: "grey.800",
		width: "195px",
	},
	"& .MuiMenuItem-gutters": {
		fontSize: theme.typography.p2.fontSize,
	},
};
